import React, { useState, Component } from 'react';
import { StarIcon, SearchIcon, ChevronDownIcon, EditIcon, TrashIcon, Share2Icon, BookOpenIcon, QuoteIcon, HeartIcon, AlertTriangleIcon, BarChart2Icon, ClockIcon, TrendingUpIcon, PlusIcon, PackageIcon, StoreIcon, HeadphonesIcon, BoxIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { WriteReviewModal, ReviewDraft, ReviewSubjectType } from '../components/Reviews/WriteReviewModal';
import { ReviewTagChip } from '../components/Reviews/ReviewTagChip';
// --- Mock Data ---
const initialReviews: ReviewDraft[] = [{
  id: 1,
  subject: {
    id: 'b1',
    type: 'book',
    title: 'The Silent Echo',
    subtitle: 'Elena Richards',
    image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  rating: 4,
  categoryRatings: {
    Writing: 4.5,
    Plot: 4,
    Pacing: 3.5,
    Characters: 5,
    Enjoyment: 4
  },
  review: "A gripping thriller that kept me on the edge of my seat. The character development was excellent. The protagonist's journey felt authentic, and the plot twists were genuinely surprising. I would have preferred a slightly faster pace in the middle section, but the climax was worth the wait.",
  date: 'Oct 1, 2023',
  moodTags: ['Tense', 'Mysterious', 'Thought-provoking'],
  pacingTags: ['Medium-paced', 'Builds slowly'],
  contentWarnings: ['Violence', 'Death', 'Grief'],
  favoriteQuote: 'Sometimes the loudest echoes come from the words we never said.'
}, {
  id: 2,
  subject: {
    id: 'b2',
    type: 'book',
    title: 'Beyond the Veil',
    subtitle: 'Marcus Chen',
    image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  rating: 5,
  categoryRatings: {
    Writing: 5,
    Plot: 5,
    Pacing: 4.5,
    Characters: 5,
    Enjoyment: 5
  },
  review: "Beautiful world-building and engaging story. Couldn't put it down! The author has created such an immersive fantasy world that feels both fresh and familiar. The magic system is intricate but explained well, and the character arcs are satisfying. This might be the best book in the series so far.",
  date: 'Sep 28, 2023',
  moodTags: ['Magical', 'Adventurous', 'Emotional'],
  pacingTags: ['Fast-paced', 'Page-turner'],
  contentWarnings: ['Mild violence'],
  favoriteQuote: 'Magic exists in the spaces between what we know and what we fear to discover.'
}, {
  id: 3,
  subject: {
    id: 'm1',
    type: 'merch',
    title: 'Fantasy Realms Bookmark Set',
    subtitle: 'Bookish Designs Co.',
    image: 'https://images.unsplash.com/photo-1544947987-f6c4fa25fa4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
  },
  rating: 5,
  categoryRatings: {
    Quality: 5,
    Value: 4,
    Shipping: 5,
    Design: 5
  },
  review: 'These bookmarks are absolutely gorgeous! The gold foil details catch the light beautifully, and the cardstock is very thick and durable. They arrived quickly and were packaged with such care. Will definitely be ordering more from this shop.',
  date: 'Sep 15, 2023'
}];
// --- Helper Components ---
const RatingBar = ({
  label,
  value
}: {
  label: string;
  value: number;
}) => <div className="flex items-center text-xs mb-1" data-id="element-10669" data-mp="1qhtx9j6hf926">
    <span className="w-24 opacity-70" data-id="element-10670" data-mp="1lk1v7wc4i6k9">{label}</span>
    <div className="h-2 w-24 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-10671" data-mp="1ljiozcvu56m2">
      <div className="h-full bg-amber-500 rounded-full" style={{
      width: `${value / 5 * 100}%`
    }} data-id="element-10672" data-mp="207mxxp5363rn" />
    </div>
    <span className="ml-2 opacity-70" data-id="element-10673" data-mp="299mg4kru7dw6">{value}</span>
  </div>;
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'book':
      return <BookOpenIcon className="h-4 w-4" data-id="element-10674" data-mp="24buolrv3ttsi" />;
    case 'merch':
      return <PackageIcon className="h-4 w-4" data-id="element-10675" data-mp="2lm6f4ih79hm" />;
    case 'bookstore':
      return <StoreIcon className="h-4 w-4" data-id="element-10676" data-mp="2jjg0jdevecd" />;
    case 'audiobook':
      return <HeadphonesIcon className="h-4 w-4" data-id="element-10677" data-mp="7j5qil1uughh" />;
    case 'box':
      return <BoxIcon className="h-4 w-4" data-id="element-10678" data-mp="cankuq9dhith" />;
    default:
      return <StarIcon className="h-4 w-4" data-id="element-10679" data-mp="cda07c2i92m9" />;
  }
};
const MyReviews = () => {
  const dk = useDarkClasses();
  const [reviews, setReviews] = useState<ReviewDraft[]>(initialReviews);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [ratingFilter, setRatingFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState<ReviewDraft | undefined>(undefined);
  // Filter & Sort Logic
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = review.subject.title.toLowerCase().includes(searchQuery.toLowerCase()) || review.subject.subtitle && review.subject.subtitle.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRating = ratingFilter === 'all' || Math.floor(review.rating).toString() === ratingFilter;
    const matchesType = typeFilter === 'all' || review.subject.type === typeFilter;
    const matchesTag = !tagFilter || review.moodTags && review.moodTags.includes(tagFilter) || review.pacingTags && review.pacingTags.includes(tagFilter) || review.contentWarnings && review.contentWarnings.includes(tagFilter);
    return matchesSearch && matchesRating && matchesType && matchesTag;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'title':
        return a.subject.title.localeCompare(b.subject.title);
      default:
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });
  // Stats
  const stats = {
    total: reviews.length,
    avgRating: reviews.length ? (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1) : '0.0',
    booksReviewed: new Set(reviews.filter(r => r.subject.type === 'book').map(r => r.subject.title)).size,
    mostRecent: reviews.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]?.date || 'None'
  };
  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };
  const handleSaveReview = (draft: ReviewDraft) => {
    setReviews(prev => {
      const exists = prev.findIndex(r => r.id === draft.id);
      if (exists >= 0) {
        const newReviews = [...prev];
        newReviews[exists] = draft;
        return newReviews;
      }
      return [draft, ...prev];
    });
  };
  const handleDeleteReview = (id: number) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      setReviews(prev => prev.filter(r => r.id !== id));
    }
  };
  const openEditModal = (review: ReviewDraft) => {
    setEditingReview(review);
    setIsModalOpen(true);
  };
  const openNewModal = () => {
    setEditingReview(undefined);
    setIsModalOpen(true);
  };
  return <div className={`min-h-screen pb-12 ${dk.page}`} data-id="element-10680" data-mp="2hd5imppb87k0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-10681" data-mp="2w4xwex9ghdjm">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-10682" data-mp="2rc4ueh5dgjb8">
          <div data-id="element-10683" data-mp="23mttmzo0uox3">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-10684" data-mp="23mahjzl1yuqz">
              My Reviews
            </h1>
            <p className={`mt-1 text-lg ${dk.textSecondary}`} data-id="element-10685" data-mp="1ynz6jlj0ue76">
              Your thoughts on books, merch, and more
            </p>
          </div>
          <button onClick={openNewModal} className="flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-10686" data-mp="1i40k8thni3tf">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-10687" data-mp="1s3829tzbymmv" />
            Write a Review
          </button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-id="element-10688" data-mp="1w27b3po6n3jl">
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-10689" data-mp="211jzieupcioa">
            <div className="flex items-center justify-between" data-id="element-10690" data-mp="25uw9biuq2kwz">
              <div data-id="element-10691" data-mp="2asy0ys67rsk2">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-10692" data-mp="2at7jxvsuczg9">
                  Total Reviews
                </p>
                <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-10693" data-mp="2jtcq62matrsu">
                  {stats.total}
                </p>
              </div>
              <div className={`p-2 rounded-full ${dk.bgMuted}`} data-id="element-10694" data-mp="87u4so7jcu2l">
                <StarIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-10695" data-mp="445sxtbvfcbj" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-10696" data-mp="3vc1jv1s937ue">
            <div className="flex items-center justify-between" data-id="element-10697" data-mp="3qcf6mscf9rll">
              <div data-id="element-10698" data-mp="3qe8g0fef6pxa">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-10699" data-mp="ru08iinkrezh">
                  Avg Rating
                </p>
                <p className="text-2xl font-bold mt-1 text-amber-600" data-id="element-10700" data-mp="mxj7woz6v85i">
                  {stats.avgRating}★
                </p>
              </div>
              <div className="p-2 rounded-full bg-amber-100" data-id="element-10701" data-mp="d4c5nczqw89n">
                <TrendingUpIcon className="h-5 w-5 text-amber-600" data-id="element-10702" data-mp="1akrdl80j4j82" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-10703" data-mp="15tj1u5bq8lrq">
            <div className="flex items-center justify-between" data-id="element-10704" data-mp="3ahcqew2lrig7">
              <div data-id="element-10705" data-mp="3ahl2ilqwijqc">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-10706" data-mp="3aj5woa9b00no">
                  Books Reviewed
                </p>
                <p className="text-2xl font-bold mt-1 text-blue-600" data-id="element-10707" data-mp="3kgttr6udtk90">
                  {stats.booksReviewed}
                </p>
              </div>
              <div className="p-2 rounded-full bg-blue-100" data-id="element-10708" data-mp="3tfowua24qe83">
                <BookOpenIcon className="h-5 w-5 text-blue-600" data-id="element-10709" data-mp="3te46yxqrfjgk" />
              </div>
            </div>
          </div>
          <div className={`p-4 rounded-lg border ${dk.card} ${dk.border}`} data-id="element-10710" data-mp="70mbtssyfx2w">
            <div className="flex items-center justify-between" data-id="element-10711" data-mp="72p0a8egli5i">
              <div data-id="element-10712" data-mp="22uay9ac10mk">
                <p className={`text-xs font-medium uppercase tracking-wider ${dk.textMuted}`} data-id="element-10713" data-mp="22b3jmib5ums">
                  Most Recent
                </p>
                <p className="text-lg font-bold mt-1 text-green-600 truncate" data-id="element-10714" data-mp="c10aik8o1uo9">
                  {stats.mostRecent}
                </p>
              </div>
              <div className="p-2 rounded-full bg-green-100" data-id="element-10715" data-mp="zmu7dp2qtv58">
                <ClockIcon className="h-5 w-5 text-green-600" data-id="element-10716" data-mp="zmaz702wal4e" />
              </div>
            </div>
          </div>
        </div>

        {/* Type Tabs */}
        <div className={`flex space-x-2 mb-6 overflow-x-auto pb-2 scrollbar-hide`} data-id="element-10717" data-mp="qlmly7lbsosk">
          {['all', 'book', 'merch', 'bookstore', 'audiobook', 'box'].map(type => <button key={type} onClick={() => setTypeFilter(type)} className={`flex items-center px-4 py-2 rounded-full text-sm font-medium capitalize whitespace-nowrap transition-colors border ${typeFilter === type ? 'bg-indigo-600 border-indigo-600 text-white' : `${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.bgMuted}`}`} data-id="element-10718" data-mp="gohv3ugjc55f">
                {type !== 'all' && <span className="mr-2 opacity-80" data-id="element-10719" data-mp="3u2czd7j8m44a">{getTypeIcon(type)}</span>}
                {type === 'all' ? 'All Reviews' : type + 's'}
              </button>)}
        </div>

        {/* Filters Bar */}
        <div className={`rounded-lg shadow-sm border p-4 mb-6 ${dk.card} ${dk.border}`} data-id="element-10720" data-mp="1qu2un2141h3x">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between" data-id="element-10721" data-mp="1lwarfbv0m8kf">
            {/* Search & Sort */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto flex-1" data-id="element-10722" data-mp="2ikz4ruvsd7ni">
              <div className="relative flex-grow max-w-md" data-id="element-10723" data-mp="2ekoptsq0n6wi">
                <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${dk.textMuted}`} data-id="element-10724" data-mp="29kkhipz9hihd" />
                <input type="text" placeholder="Search title or author..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-10725" data-mp="24lhfleekvsdm" />
              </div>

              <div className="relative" data-id="element-10726" data-mp="o29r1v5qhm58">
                <button onClick={() => setShowSortDropdown(!showSortDropdown)} className={`flex items-center justify-between w-full sm:w-40 px-3 py-2 border rounded-md text-sm font-medium ${dk.input}`} data-id="element-10727" data-mp="t1cy1l59y2fu">
                  <span data-id="element-10728" data-mp="11t53fhffyldk">
                    Sort: {sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 ml-2 opacity-50" data-id="element-10729" data-mp="16uavdd1a24m1" />
                </button>
                {showSortDropdown && <div className={`absolute z-10 mt-1 w-full rounded-md shadow-lg border ${dk.card} ${dk.border}`} data-id="element-10730" data-mp="1gqxenxdygnly">
                    {['date', 'rating', 'title'].map(option => <button key={option} className={`block w-full text-left px-4 py-2 text-sm hover:${dk.bgMuted} ${sortBy === option ? 'font-bold text-indigo-600' : dk.textPrimary}`} onClick={() => {
                  setSortBy(option);
                  setShowSortDropdown(false);
                }} data-id="element-10731" data-mp="3iikb4egm9apt">
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </button>)}
                  </div>}
              </div>
            </div>

            {/* Rating Filter Tabs */}
            <div className="w-full lg:w-auto overflow-x-auto pb-2 lg:pb-0 scrollbar-hide" data-id="element-10732" data-mp="12vu3j57ycz3g">
              <div className="flex space-x-2" data-id="element-10733" data-mp="y4c1sey3uuhf">
                {['all', '5', '4', '3', '2', '1'].map(rating => <button key={rating} onClick={() => setRatingFilter(rating)} className={`px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${ratingFilter === rating ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300' : `${dk.textMuted} hover:${dk.bgMuted}`}`} data-id="element-10734" data-mp="ae81agg7raun">
                    {rating === 'all' ? 'All Ratings' : `${rating}★`}
                  </button>)}
              </div>
            </div>
          </div>
        </div>

        {/* Active Tag Filter Banner */}
        {tagFilter && <div className={`flex items-center justify-between p-3 mb-6 rounded-lg border bg-indigo-50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-800/30`} data-id="element-10735" data-mp="30pesn9e3rvey">
            <div className="flex items-center gap-2" data-id="element-10736" data-mp="2qvfrodprmhol">
              <span className="text-sm font-medium text-indigo-800 dark:text-indigo-300" data-id="element-10737" data-mp="2vrmw222ijl4f">
                Filtering by tag:
              </span>
              <ReviewTagChip label={tagFilter} variant="neutral" data-id="element-10738" data-mp="1fg9ab9l2d98d" />
            </div>
            <button onClick={() => setTagFilter(null)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300" data-id="element-10739" data-mp="1keki7erst9ny">
              Clear Filter
            </button>
          </div>}

        {/* Reviews List */}
        <div className="space-y-4" data-id="element-10740" data-mp="rgqibbc596jb">
          {filteredReviews.length === 0 ? <div className={`text-center py-12 rounded-lg border border-dashed ${dk.border} ${dk.bgMuted}/30`} data-id="element-10741" data-mp="mjhvuyjyqgc9">
              <SearchIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-10742" data-mp="cltzy5amf2si" />
              <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-10743" data-mp="cnwzph1ksaaf">
                No reviews found
              </h3>
              <p className={`mt-1 text-sm ${dk.textMuted}`} data-id="element-10744" data-mp="3l5nhf0vlahq">
                Try adjusting your search or filters.
              </p>
              <button onClick={() => {
            setSearchQuery('');
            setRatingFilter('all');
            setTypeFilter('all');
            setSortBy('date');
            setTagFilter(null);
          }} className="mt-4 text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-10745" data-mp="22wov9qag0cpt">
                Clear all filters
              </button>
            </div> : filteredReviews.map(review => <div key={review.id} className={`rounded-lg shadow-sm border overflow-hidden transition-all duration-200 ${dk.card} ${dk.border}`} data-id="element-10746" data-mp="2k6srap03xd0u">
                {/* Collapsed Header Row */}
                <div onClick={() => toggleExpand(review.id!)} className={`p-4 cursor-pointer flex items-center gap-4 hover:${dk.bgMuted}/50 transition-colors`} data-id="element-10747" data-mp="1whrd0l4lf7nn">
                  {review.subject.image ? <img src={review.subject.image} alt={review.subject.title} className="h-16 w-12 rounded object-cover shadow-sm flex-shrink-0" data-id="element-10748" data-mp="37wd9v6scm89l" /> : <div className={`h-16 w-12 rounded flex items-center justify-center flex-shrink-0 ${dk.bgMuted}`} data-id="element-10749" data-mp="3t1h2sq61lui6">
                      {getTypeIcon(review.subject.type)}
                    </div>}

                  <div className="flex-1 min-w-0" data-id="element-10750" data-mp="6rwcd1u2ix4v">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1" data-id="element-10751" data-mp="kk46smxvqc0e">
                      <div data-id="element-10752" data-mp="fl12j4pq49th">
                        <div className="flex items-center gap-2" data-id="element-10753" data-mp="flsyign8g9ka">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${dk.bgMuted} ${dk.textSecondary}`} data-id="element-10754" data-mp="uia8b3frxg4q">
                            {review.subject.type}
                          </span>
                          <h3 className={`font-medium text-base truncate ${dk.textHeading}`} data-id="element-10755" data-mp="2rcws2qr2y28i">
                            {review.subject.title}
                          </h3>
                        </div>
                        {review.subject.subtitle && <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-10756" data-mp="x3hohix53z4l">
                            {review.subject.subtitle}
                          </p>}
                      </div>
                      <div className="flex items-center gap-3" data-id="element-10757" data-mp="o55ztmfu3bes">
                        <div className="flex text-amber-400" data-id="element-10758" data-mp="s5g4mvrw3vs2">
                          {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < Math.floor(review.rating) ? 'fill-current' : i < review.rating ? 'fill-current opacity-50' : 'text-gray-300 dark:text-gray-600'}`} data-id="element-10759" data-mp="e6q4t8dori00" data-mpi={i} />)}
                        </div>
                        <span className={`text-xs ${dk.textMuted} hidden sm:block`} data-id="element-10760" data-mp="3h1nr0wn108na">
                          {review.date}
                        </span>
                      </div>
                    </div>
                  </div>

                  <ChevronDownIcon className={`h-5 w-5 ${dk.textMuted} transition-transform duration-200 ${expandedId === review.id ? 'transform rotate-180' : ''}`} data-id="element-10761" data-mp="3c2tbc3kcv07e" />
                </div>

                {/* Expanded Content */}
                {expandedId === review.id && <div className={`px-4 pb-6 pt-2 border-t ${dk.border} ${dk.bgMuted}/10`} data-id="element-10762" data-mp="3qsihyeb7vwf2">
                    <div className="pl-16" data-id="element-10763" data-mp="3vpikwgcrsj1y">
                      {/* Review Text */}
                      <p className={`text-sm leading-relaxed mb-4 whitespace-pre-wrap ${dk.textPrimary}`} data-id="element-10764" data-mp="2fbsj5g94i6vh">
                        {review.review}
                      </p>

                      {/* Quote */}
                      {review.favoriteQuote && <div className={`mb-4 pl-4 border-l-2 border-indigo-300 italic text-sm ${dk.textSecondary} flex gap-2`} data-id="element-10765" data-mp="27sj8zkssm45d">
                          <QuoteIcon className="h-4 w-4 opacity-50 flex-shrink-0 mt-1" data-id="element-10766" data-mp="2gv0t9tow0mpq" />
                          <p data-id="element-10767" data-mp="1t3n0jmmtqp48">"{review.favoriteQuote}"</p>
                        </div>}

                      {/* Tags */}
                      {review.moodTags?.length || review.pacingTags?.length || review.contentWarnings?.length ? <div className="flex flex-wrap gap-2 mb-6" data-id="element-10768" data-mp="1y37xjrifoneb">
                          {review.moodTags?.map(tag => <ReviewTagChip key={tag} label={tag} variant="mood" onClick={setTagFilter} data-id="element-10769" data-mp="1acccreceasgk" />)}
                          {review.pacingTags?.map(tag => <ReviewTagChip key={tag} label={tag} variant="pacing" onClick={setTagFilter} data-id="element-10770" data-mp="3f751415udu5d" />)}
                          {review.contentWarnings?.map(tag => <ReviewTagChip key={tag} label={tag} variant="warning" onClick={setTagFilter} data-id="element-10771" data-mp="2rhu670b6atz5" />)}
                        </div> : null}

                      {/* Category Ratings */}
                      {Object.keys(review.categoryRatings).length > 0 && <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 mb-6 pt-4 border-t ${dk.border}`} data-id="element-10772" data-mp="pvy7biq6o1ib">
                          {Object.entries(review.categoryRatings).map(([label, val]) => <RatingBar key={label} label={label} value={val} data-id="element-10773" data-mp="c537sx1kqwre" />)}
                        </div>}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-4 pt-2" data-id="element-10774" data-mp="3tehtq4m6kd57">
                        <button onClick={() => openEditModal(review)} className="flex items-center text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400" data-id="element-10775" data-mp="3pc4vs2dodjiz">
                          <EditIcon className="h-3 w-3 mr-1.5" data-id="element-10776" data-mp="1umn7g7szeu6t" /> Edit Review
                        </button>
                        <button onClick={() => handleDeleteReview(review.id!)} className="flex items-center text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400" data-id="element-10777" data-mp="1zlhu5ku2l2w7">
                          <TrashIcon className="h-3 w-3 mr-1.5" data-id="element-10778" data-mp="1plqy3epv2fu0" /> Delete
                        </button>
                        <button className={`flex items-center text-xs font-medium ${dk.textSecondary} hover:${dk.textPrimary}`} data-id="element-10779" data-mp="1bu2llabp9pci">
                          <Share2Icon className="h-3 w-3 mr-1.5" data-id="element-10780" data-mp="11zkf5xewffp0" /> Share
                        </button>
                      </div>
                    </div>
                  </div>}
              </div>)}
        </div>
      </div>

      <WriteReviewModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} existingReview={editingReview} onSave={handleSaveReview} data-id="element-10781" data-mp="xwnpjmqxyjbk" />
    </div>;
};
export default MyReviews;